import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';

import {Icons} from '../../assets';
import {CandidateCard, DashboardLayout, EmptyState} from '../../components';
import {Button, ButtonVariant} from '../../components/button';
import {getPublicProfilePath, ROUTES} from '../../constants/routes';
import {useEmployerCandidate} from '../../contexts/employer-candidate.provider';
import {SavedCandidate} from '../../models';
import {ListSavedCandidatesParams} from '../../services';

const DEFAULT_LIMIT = 12;

const SavedCandidatesPage: React.FC = () => {
  const navigate = useNavigate();
  const {listSavedCandidates, removeSavedCandidate} = useEmployerCandidate();
  const [bookmarkingCandidates, setBookmarkingCandidates] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [savedCandidates, setSavedCandidates] = useState<SavedCandidate[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const fetchSavedCandidates = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await listSavedCandidates(new ListSavedCandidatesParams({limit: DEFAULT_LIMIT, page}));
      setSavedCandidates(response.data);
      setTotal(response.total);
      setTotalPages(response.totalPages);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to load saved candidates. Please refresh the page.';
      setError(message);
      setSavedCandidates([]);
    } finally {
      setIsLoading(false);
    }
  }, [listSavedCandidates, page]);

  useEffect(() => {
    fetchSavedCandidates();
  }, [fetchSavedCandidates]);

  const handleBookmarkToggle = async (savedCandidate: SavedCandidate) => {
    const candidateId = savedCandidate.candidateProfile.id;
    if (!candidateId) {
      return;
    }

    setBookmarkingCandidates(prev => ({...prev, [candidateId]: true}));
    setError(null);
    try {
      await removeSavedCandidate(savedCandidate.id);
      setSavedCandidates(prev => prev.filter(candidate => candidate.id !== savedCandidate.id));
      setTotal(prev => Math.max(prev - 1, 0));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to remove this saved candidate. Please try again or refresh the page.';
      setError(message);
    } finally {
      setBookmarkingCandidates(prev => ({...prev, [candidateId]: false}));
    }
  };

  const handlePageChange = (nextPage: number) => {
    if (nextPage < 1 || nextPage > totalPages) {
      return;
    }
    setPage(nextPage);
  };

  const hasResults = savedCandidates.length > 0;
  const showEmptyState = !isLoading && !hasResults;
  const hasNextPage = page < totalPages;
  const hasPreviousPage = page > 1;

  return (
    <DashboardLayout
      breadcrumbs={[
        {label: 'Dashboard', path: ROUTES.DASHBOARD},
        {label: 'Search Candidates', path: ROUTES.SEARCH_CANDIDATES},
        {label: 'Saved Candidates'},
      ]}
      className="bg-[#F8FAFF]"
      contentPaddingClassName="px-0"
      sidebarOffsetClassName="ml-0">
      <div className="w-full px-6 pb-20 pt-12">
        <div className="ml-20 flex flex-col gap-2 text-gray-800">
          <h1 className="text-3xl font-semibold text-gray-900">Saved Candidates ({total})</h1>
          <p className="text-sm text-gray-500">Review the profiles you have bookmarked while searching Aura.</p>
        </div>

        {error && (
          <div className="ml-20 mt-6">
            <div className="rounded-2xl border border-red-100 bg-red-50 px-6 py-4 text-sm text-red-600 shadow-sm">{error}</div>
          </div>
        )}

        {isLoading && (
          <div className="ml-20 mt-10 rounded-2xl border border-dashed border-gray-200 bg-white px-8 py-14 text-center text-gray-500">
            Loading saved candidates...
          </div>
        )}

        {showEmptyState && (
          <div className="mt-16 flex min-h-[50vh] w-full items-center justify-center">
            <EmptyState
              icon={Icons.BOOKMARK}
              title="No saved candidates yet"
              description="You haven't bookmarked any profiles yet. Head back to search and start saving promising candidates to review them here."
              actionLabel="Search candidates"
              onAction={() => navigate(ROUTES.SEARCH_CANDIDATES)}
            />
          </div>
        )}

        {hasResults && !isLoading && (
          <div className="ml-20 mt-8 grid gap-4 md:grid-cols-2 md:gap-5 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
            {savedCandidates.map(savedCandidate => {
              const candidate = savedCandidate.candidateProfile;
              const candidateId = candidate.id;
              const profileAccountId = candidate.accountId;
              const profilePath = profileAccountId
                ? getPublicProfilePath(profileAccountId, candidateId ? {candidateId, fromSearch: true} : {fromSearch: true})
                : null;
              const navigateToProfile = () => {
                if (!profilePath) return;
                window.open(profilePath, '_blank', 'noopener,noreferrer');
              };
              return (
                <CandidateCard
                  candidate={candidate}
                  isBookmarked
                  isBookmarking={Boolean(candidateId && bookmarkingCandidates[candidateId])}
                  key={savedCandidate.id}
                  onBookmarkToggle={() => handleBookmarkToggle(savedCandidate)}
                  onViewAura={profilePath ? navigateToProfile : undefined}
                />
              );
            })}
          </div>
        )}

        {hasResults && totalPages > 1 && (
          <div className="ml-20 mt-10 flex flex-col items-center gap-3 rounded-2xl bg-white px-6 py-5 text-sm text-gray-600 shadow-sm md:flex-row md:justify-between">
            <span>
              Page {page} of {totalPages}
            </span>
            <div className="flex gap-3">
              <Button disabled={!hasPreviousPage || isLoading} onClick={() => handlePageChange(page - 1)} variant={ButtonVariant.Outline}>
                Previous
              </Button>
              <Button disabled={!hasNextPage || isLoading} onClick={() => handlePageChange(page + 1)} variant={ButtonVariant.Primary}>
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SavedCandidatesPage;
